import { Icon } from "@/components/common/icon/Icon";
import { HistoryRecord } from "@/modules/user/types/history";
import { IconType } from "@/components/common/icon/Icon";
import { useTranslation } from "react-i18next";
import {
  formatDateRecordLocalized,
  formatDateRecordLocalizedDateOnly,
  formatNumber,
} from "@/modules/auth/utils/format";
import TransactionRecord from "../modal/TransactionRecord";
import { useState } from "react";
import i18n from "@/i18n";
import {
  isDailyReward,
  isDeposit,
  isDepositRefReward,
  isDepositReward,
  isDepositRewardVesting,
  isWithdrawal,
  isWithdrawalRequest,
  isWTTBonus,
  isMatchingBonus,
  isWithdrawalMatchingBonus,
  isReinvest,
} from "@/modules/auth/utils/history";
import { constants } from "@/modules/calculations/consts";
import { fromNano } from "@ton/core";

const typeToIconMap: Record<string, IconType> = {
  dep: "arrow-down-circle-fill",
  "dep-ref-reward": "heart",
  "dep-reward-daily": "arrow-down-circle-fill",
  "dep-reward": "arrow-up-circle-fill",
  "dep-reward-vesting": "arrow-up-circle-fill",
  withdrawal: "arrow-up-circle-fill",
  "withdrawal-dep-ref-reward": "arrow-up-circle-fill",
  "withdrawal-dep-reward": "arrow-up-circle-fill",
  "withdrawal-wtt-bonus": "arrow-up-circle-fill",
  "wtt-bonus": "arrow-up-circle-fill",
  "withdrawal-dep-reward-ref-bonus": "arrow-up-circle-fill",
  "dep-reward-ref-bonus": "arrow-up-circle-fill",
};

const Transaction = (item: HistoryRecord) => {
  const { t } = useTranslation();

  const [isOpened, setIsOpened] = useState(false);

  const status =
    item.type !== "withdrawal"
      ? item.type
      : "type" in item.details
      ? `withdrawal-${item.details.type}`
      : "withdrawal";
  const icon = typeToIconMap[status];

  let tonAmount = 0;
  let fromAddress: string | undefined;
  let toAddress: string | undefined;
  let btnAmount = undefined;
  let usdAmount = undefined;
  let transaction: string | undefined;
  const comission =
    !isWTTBonus(item.details) &&
    !isDailyReward(item.details) &&
    "feeTonAmount" in item.details
      ? item.details.feeTonAmount
      : 0;

  if (isReinvest(item.details)) {
    btnAmount = item.details.withdrawableTokenAmount || 0;
  } else if (isMatchingBonus(item.details)) {
    btnAmount = item.details.tokenAmount || 0;
  } else if (isWithdrawalMatchingBonus(item.details)) {
    tonAmount = item.details.tonAmount;
  } else if (isDeposit(item.details)) {
    tonAmount = item.details.tonAmount;
    fromAddress = item.details.fromAddress || undefined;
    toAddress = item.details.toAddress || undefined;
    btnAmount = item.details.tokenAmount;
    usdAmount = item.details.usdAmount;
    transaction = item.details.transaction || undefined;
  } else if (isDepositRefReward(item.details)) {
    if (item.details.network === "EVM") {
      btnAmount = item.details.amount;
    } else {
      tonAmount = item.details.amount;
    }
  } else if (isDepositReward(item.details)) {
    tonAmount = item.details.withdrawableTonAmount || 0;
    btnAmount = item.details.withdrawableTokenAmount || 0;
    usdAmount = item.details.withdrawableUsdAmount || 0;
  } else if (isDepositRewardVesting(item.details)) {
    tonAmount = item.details.tonAmount || 0;
    btnAmount = item.details.tokenAmount || 0;
    usdAmount = item.details.usdAmount || 0;
  } else if (isWithdrawalRequest(item.details)) {
    btnAmount = Number(fromNano(item.details.request?.amount || 0)) * 1000;
    toAddress = item.details.toAddress || undefined;
    transaction = item.details.transaction || undefined;
  } else if (isWithdrawal(item.details)) {
    tonAmount = item.details.tonAmount - comission;
    toAddress = item.details.toAddress || undefined;
    transaction = item.details.transaction || undefined;
  } else if (isWTTBonus(item.details)) {
    tonAmount = item.details.tonAmount || 0;
    usdAmount = item.details.usdAmount || 0;
  } else if (isDailyReward(item.details)) {
    btnAmount = item.details.tokenAmount || 0;
  }

  const isPositive =
    item.type === "dep" ||
    item.type === "dep-ref-reward" ||
    item.type === "dep-reward-daily" ||
    item.type === "wtt-bonus" ||
    item.type === "dep-reward-ref-bonus";
  const sign = isPositive ? "+" : "–";
  const amountClass = isPositive ? "text-success" : "";

  const btnAmountString = `${sign}${formatNumber(btnAmount || 0, {
    precision: constants.TOKEN_DECIMALS,
  })} BTN`;
  const tonAmountString = `${sign}${formatNumber(tonAmount, {
    precision: constants.TON_DECIMALS,
  })} TON`;
  const usdAmountString = `$${formatNumber(usdAmount || 0)}`;

  const openModal = () => {
    setIsOpened(true);
  };

  const closeModal = () => {
    setIsOpened(false);
  };

  return (
    <>
      <button onClick={openModal} className="flex items-center gap-4 px-4">
        <Icon type={icon} className="shrink-0 w-7 h-7" />

        <div className="flex-1 flex flex-col items-start py-3 gap-0.5 text-left">
          <p className="text-[1.0625rem] leading-[1.375rem] font-medium">
            {"status" in item.details && item.details.status === "Reinvest"
              ? t(`history-page.status.reward-reinv`)
              : t(`history-page.status.${status}`)}
          </p>
          <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
            {item.type === "dep-reward-daily"
              ? formatDateRecordLocalizedDateOnly(item.createdAt, i18n.language)
              : formatDateRecordLocalized(item.createdAt, i18n.language)}
          </p>
          {Boolean(comission && comission > 0) && (
            <p className="text-[0.8125rem] leading-[1rem] text-subtitle-text">
              {t("history-page.comission")}: {comission} TON
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-0.5 text-right">
          {btnAmount != undefined && usdAmount != undefined ? (
            <>
              <p
                className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}
              >
                {btnAmountString}
              </p>
              <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
                {usdAmountString}
              </p>
            </>
          ) : isDepositRefReward(item.details) &&
            item.details.network === "EVM" ? (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {btnAmountString}
            </p>
          ) : isDepositRefReward(item.details) &&
            item.details.network === "TON" ? (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {tonAmountString}
            </p>
          ) : isWTTBonus(item.details) ? (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {usdAmountString}
            </p>
          ) : isWithdrawalMatchingBonus(item.details) ? (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {tonAmountString}
            </p>
          ) : isDailyReward(item.details) ||
            isWithdrawalRequest(item.details) ||
            isReinvest(item.details) ? (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {btnAmountString}
            </p>
          ) : (
            <p className={`text-[1.0625rem] leading-[1.375rem] ${amountClass}`}>
              {tonAmountString}
            </p>
          )}
        </div>
      </button>

      {isOpened && (
        <TransactionRecord
          title={
            status === "dep" &&
            "transaction" in item.details &&
            item.details.transaction === "Reinvest"
              ? t(`history-page.status.reinv`)
              : status === "dep-reward" &&
                "status" in item.details &&
                item.details.status === "Reinvest"
              ? t(`history-page.status.reward-reinv`)
              : t(`history-page.status.${status}`)
          }
          btnAmountString={btnAmountString}
          usdAmountString={usdAmountString}
          btnAmount={btnAmount}
          usdAmount={usdAmount}
          tonAmount={tonAmount}
          date={item.createdAt}
          from={fromAddress}
          to={toAddress}
          transaction={transaction}
          comission={comission}
          typeRecord={item.type}
          onClose={closeModal}
          details={item.details}
        />
      )}
    </>
  );
};

export default Transaction;
